import type { LoginComplianceProfile } from "./loginComplianceProfile";

export type LoginTrustClaimStatus =
  | "verified"
  | "aligned"
  | "in_progress"
  | "hidden";

export type LoginTrustClaimSurface = "trust_panel" | "footer" | "both";

export type LoginTrustClaim = {
  id: string;
  i18nKey: string;
  status: LoginTrustClaimStatus;
  profiles?: LoginComplianceProfile[];
  link?: string;
  surface?: LoginTrustClaimSurface;
  footerPending?: boolean;
  stripIcon?: string;
  tooltipI18nKey?: string;
};

export type PublicComplianceClaim = {
  id: string;
  status: LoginTrustClaimStatus;
  effective_date?: string | null;
};

export const LOGIN_TRUST_CLAIMS: readonly LoginTrustClaim[] = [
  {
    id: "eeg_turnaround",
    i18nKey: "ui.copy.c4e8f1a92b70",
    status: "verified",
    surface: "trust_panel",
  },
  {
    id: "iso_13485",
    i18nKey: "ui.copy.cba3f8168223",
    status: "aligned",
    link: "/compliance",
    surface: "both",
  },
  {
    id: "gdpr",
    i18nKey: "ui.copy.623a96e05cb5",
    status: "aligned",
    profiles: ["eu_gdpr", "global"],
    link: "/privacy-policy",
    surface: "both",
  },
  {
    id: "hipaa_trust",
    i18nKey: "ui.copy.7c1d2e3f4a5b",
    status: "aligned",
    profiles: ["us_hipaa", "global"],
    link: "/hipaa-notice",
    surface: "trust_panel",
  },
  {
    id: "rbac_audit",
    i18nKey: "ui.copy.a44901491662",
    status: "verified",
    surface: "trust_panel",
  },
  {
    id: "fda_aligned",
    i18nKey: "ui.copy.2283fc074981",
    status: "in_progress",
    link: "/compliance",
    surface: "trust_panel",
  },
  {
    id: "hipaa_footer",
    i18nKey: "ui.copy.cf9a94999f92",
    status: "aligned",
    profiles: ["us_hipaa", "global"],
    link: "/hipaa-notice",
    surface: "footer",
    stripIcon: "bi bi-shield-lock-fill",
    tooltipI18nKey: "ui.copy.179df1fa7b28",
  },
  {
    id: "iso_footer",
    i18nKey: "ui.copy.d343f9954175",
    status: "aligned",
    link: "/compliance",
    surface: "footer",
    stripIcon: "bi bi-patch-check-fill",
    tooltipI18nKey: "ui.copy.95fec7e1e9be",
  },
  {
    id: "soc2_footer",
    i18nKey: "ui.copy.2776e78c44fd",
    status: "in_progress",
    surface: "footer",
    footerPending: true,
    stripIcon: "bi bi-clock-history",
    tooltipI18nKey: "ui.copy.d4efbaf6ac5f",
  },
  {
    id: "gdpr_footer",
    i18nKey: "ui.copy.a5b7d57a8574",
    status: "aligned",
    profiles: ["eu_gdpr", "global"],
    link: "/privacy-policy",
    surface: "footer",
    stripIcon: "bi bi-globe2",
    tooltipI18nKey: "ui.copy.9dba82636f72",
  },
] as const;

export function mergeTrustClaims(
  claimOverrides?: PublicComplianceClaim[] | null,
): readonly LoginTrustClaim[] {
  if (!claimOverrides?.length) {
    return LOGIN_TRUST_CLAIMS;
  }

  const overrideMap = new Map(claimOverrides.map((claim) => [claim.id, claim]));
  return LOGIN_TRUST_CLAIMS.map((claim) => {
    const override = overrideMap.get(claim.id);
    if (!override) {
      return claim;
    }
    return {
      ...claim,
      status: override.status,
    };
  });
}

function claimMatchesProfile(
  claim: LoginTrustClaim,
  profile: LoginComplianceProfile,
): boolean {
  if (!claim.profiles || claim.profiles.length === 0) {
    return true;
  }
  return claim.profiles.includes(profile);
}

function claimMatchesSurface(
  claim: LoginTrustClaim,
  surface: LoginTrustClaimSurface,
): boolean {
  const claimSurface = claim.surface ?? "both";
  return claimSurface === surface || claimSurface === "both";
}

export function getTrustPanelClaims(
  profile: LoginComplianceProfile,
  claimOverrides?: PublicComplianceClaim[] | null,
): LoginTrustClaim[] {
  return mergeTrustClaims(claimOverrides).filter(
    (claim) =>
      claim.status !== "hidden" &&
      claimMatchesProfile(claim, profile) &&
      claimMatchesSurface(claim, "trust_panel"),
  );
}

export function getFooterCertificationClaims(
  profile: LoginComplianceProfile,
  claimOverrides?: PublicComplianceClaim[] | null,
): LoginTrustClaim[] {
  return mergeTrustClaims(claimOverrides).filter(
    (claim) =>
      claim.status !== "hidden" &&
      claimMatchesProfile(claim, profile) &&
      claimMatchesSurface(claim, "footer"),
  );
}

export function getComplianceStripClaims(
  profile: LoginComplianceProfile,
  claimOverrides?: PublicComplianceClaim[] | null,
): LoginTrustClaim[] {
  return getFooterCertificationClaims(profile, claimOverrides);
}

export function getTrustClaimStatusLabelKey(
  status: LoginTrustClaimStatus,
): string | null {
  switch (status) {
    case "aligned":
      return "ui.copy.1b6c7d8e9f0a";
    case "in_progress":
      return "ui.copy.2c7d8e9f0a1b";
    default:
      return null;
  }
}
